import { isEntityLoaded } from '@/Models/Entity';
import { CustomWorklist } from '@/Models/GenericWorklist/CustomWorklist';
import { Group } from '@/Models/Shared/Group';
import { UserModel } from '@/Models/User';
import { TFunction } from 'i18next';
import { action, makeAutoObservable } from 'mobx';
import { IWorklistStore } from '../../Stores/WorklistStore';
import GroupLevel from '../GroupLevel';

export class SaveWorklistDialogViewStore {
    private chosenNameValue?: string;
    private chosenDescriptionValue?: string;
    private chosenScopeValue?: GroupLevel;
    private chosenGroupID?: number;

    private readonly baseCustomWorklist?: CustomWorklist;
    private readonly translate: TFunction<'translation', undefined>;
    private readonly user: UserModel;
    private readonly worklistStore: IWorklistStore;

    constructor(
        baseCustomWorklist: CustomWorklist | undefined,
        translate: TFunction<'translation', undefined>,
        user: UserModel,
        worklistStore: IWorklistStore,
    ) {
        makeAutoObservable(this, {
            resetSelections: action,
        });

        this.baseCustomWorklist = baseCustomWorklist;
        this.translate = translate;
        this.user = user;
        this.worklistStore = worklistStore;
    }

    get description(): string | undefined {
        return this.chosenDescriptionValue ?? this.baseCustomWorklist?.Description;
    }

    get groupID(): number | undefined {
        if (this.scope !== GroupLevel.Group) {
            return undefined;
        }

        const availableOptions = this.groupOptions.filter((option) => !option.isDisabled);
        const onlyOneAvailable = availableOptions.length === 1;
        const singleAvailableOptionValue = onlyOneAvailable ? availableOptions[0].value : undefined;
        return this.chosenGroupID ?? this.baseCustomWorklist?.GroupID ?? singleAvailableOptionValue;
    }

    get groupOptions(): { value: number; label: string; isDisabled: boolean }[] {
        return this.groups
            .map((group) => ({
                value: group.ID,
                label: group.Description,
                isDisabled: !(this.user.admin || group.ID === this.user.groupID),
            }),
            );
    }

    get groupOptionSelected(): { value: number; label: string; isDisabled?: boolean } | undefined {
        return this.groupOptions.find((option) => option.value === this.groupID);
    }

    get groupSelectionVisible(): boolean {
        return this.scope === GroupLevel.Group;
    }

    get isValidToSave(): boolean {
        if (!this.name) {
            return false;
        }

        if (this.scope === GroupLevel.Group && !this.groupID) {
            return false;
        }

        if (this.scope === GroupLevel.Site && !this.user.admin) {
            return false;
        }

        return true;
    }

    get name(): string | undefined {
        return this.chosenNameValue ?? this.baseCustomWorklist?.Name;
    }

    get scope(): GroupLevel {
        const defaultScope = this.user.admin
            ? GroupLevel.Site
            : GroupLevel.User;

        return this.chosenScopeValue ?? this.baseCustomWorklist?.GroupLevel ?? defaultScope;
    }

    get scopeOptions(): { value: number; label: string; isDisabled?: boolean }[] {
        const base = [
            { value: GroupLevel.User, label: this.translate('User') },
            { value: GroupLevel.Group, label: this.translate('Group') },
        ];

        if (!this.user.admin) {
            return base;
        }

        const siteOption = { value: GroupLevel.Site, label: this.translate('Site') };
        return [...base, siteOption];
    }

    get scopeOptionSelected(): { value: number; label: string; isDisabled?: boolean } | undefined {
        return this.scopeOptions.find((option) => option.value === this.scope);
    }

    readonly resetSelections = action(() => {
        this.chosenNameValue = undefined;
        this.chosenDescriptionValue = undefined;
        this.chosenGroupID = undefined;
        this.chosenScopeValue = undefined;
    });

    readonly setDescription = action((description: string) => {
        this.chosenDescriptionValue = description;
    });

    readonly setGroupID = action((groupID: number) => {
        this.chosenGroupID = groupID;
    });

    readonly setName = action((name: string) => {
        this.chosenNameValue = name;
    });

    readonly setScope = action((scope: GroupLevel) => {
        this.chosenScopeValue = scope;
    });

    private get groups(): Group[] {
        const initialPageData = this.worklistStore.initialPageData;
        if (isEntityLoaded(initialPageData)) {
            return initialPageData.Groups;
        }

        return [];
    }
}
